import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { ALL_PRODUCTS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedDrawer({ open, onClose, saved, toggleSave, navTo }) {
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [place, setPlace] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setDone(null);
      setErr("");
    }
  }, [open]);

  const items = ALL_PRODUCTS.filter((p) => saved.includes(p.id));

  const submit = async () => {
    if (!name.trim() || !phone.trim()) {
      setErr("নাম আর ফোন নম্বরটা লিখে যান — শিল্পীরা কল করে জানাবেন।");
      return;
    }
    if (items.length === 0) {
      setErr("ব্যাগ তো ফাঁকা — আগে পছন্দের জিনিস জমান।");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/bag/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          place: place.trim(),
          message: note.trim(),
          items: items.map((p) => ({ id: p.id, title: p.title, price: p.price })),
        }),
      });
      const d = await res.json();
      if (d.ok) setDone(d.enquiry_id);
      else setErr("জিজ্ঞেসটা পৌঁছাল না — আবার চাপুন।");
    } catch {
      setErr("লাইন দুর্বল — আবার চাপুন।");
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[85]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-dusk/70 backdrop-blur-sm" onClick={onClose} />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 flex h-full w-[400px] max-w-[90vw] flex-col border-l border-gold/20 bg-plaster"
            data-testid="saved-drawer"
          >
            <div className="flex items-center justify-between border-b border-gold/15 px-5 py-4">
              <div>
                <h3 className="font-display text-xl text-sand">আপনার ব্যাগ</h3>
                <p className="font-latin text-[11px] italic tracking-wide text-gold/60">The village bag — {items.length} items</p>
              </div>
              <button
                data-testid="saved-drawer-close"
                onClick={onClose}
                className="rounded-full p-2 text-sand/60 transition-colors hover:bg-sand/10 hover:text-sand"
                aria-label="বন্ধ করুন"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <p className="font-display text-lg text-sand/70">ব্যাগ এখন ফাঁকা</p>
                  <p className="mt-2 max-w-[250px] text-sm leading-relaxed text-sand/45">
                    হাটের দোকানে পছন্দের শিল্পকর্মে হৃদয়-চিহ্নে চাপলে এখানে জমা হবে।
                  </p>
                  <button
                    data-testid="saved-empty-goto-haat"
                    onClick={() => navTo("hat", "রঙের হাট")}
                    className="mt-6 rounded-full border border-gold/40 px-5 py-2 text-sm text-gold transition-all hover:bg-gold hover:text-dusk"
                  >
                    রঙের হাটে যান
                  </button>
                </div>
              ) : (
                <>
                  <ul className="space-y-4">
                    {items.map((p) => (
                      <li
                        key={p.id}
                        className="flex gap-3 rounded-xl border border-gold/12 bg-earthen/60 p-3"
                        data-testid={`saved-item-${p.id}`}
                      >
                        <img src={p.img} alt={p.title} className="h-20 w-16 rounded-lg object-cover" />
                        <div className="flex-1">
                          <p className="text-sm leading-snug text-sand">{p.title}</p>
                          <p className="mt-0.5 text-[11px] text-sand/45">{p.artisan}</p>
                          <p className="mt-1 text-sm font-medium text-gold">{p.price}</p>
                        </div>
                        <button
                          data-testid={`saved-remove-${p.id}`}
                          onClick={() => toggleSave(p.id)}
                          className="self-start rounded-full p-1.5 text-sand/40 transition-colors hover:bg-sand/10 hover:text-vermilion"
                          aria-label="ব্যাগ থেকে বাদ দিন"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>

                  {done ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 rounded-xl border border-gold/30 bg-gold/10 p-5 text-center"
                      data-testid="enquiry-success"
                    >
                      <p className="font-display text-lg text-sand">জিজ্ঞেসটা পৌঁছে গেছে</p>
                      <p className="mt-2 text-xs leading-relaxed text-sand/70">
                        ধন্যবাদ {name.trim()} — শিল্পী বা দল ফোন করে ডেলিভারির ব্যবস্থা জানাবেন।
                      </p>
                      <p className="mt-3 inline-block rounded-full border border-gold/30 px-3 py-1 text-[11px] text-gold">
                        {done}
                      </p>
                      <p className="mt-3 text-[10px] text-sand/40">প্রোটোটাইপ — এখনই পেমেন্ট হয় না</p>
                    </motion.div>
                  ) : (
                    <div className="mt-6">
                      <label className="block text-xs text-sand/60">শিল্পীদের জন্য বার্তা</label>
                      <textarea
                        data-testid="bag-artisan-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        placeholder="যেমন — ঘোড়া দুটো নরম কাপড়ে মুড়িয়ে পাঠাবেন…"
                        className="mt-2 w-full resize-none rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                      />

                      <p className="mt-5 font-latin text-[11px] italic tracking-[0.2em] text-gold/70">DELIVERY ENQUIRY</p>
                      <p className="mt-1 text-[11px] text-sand/45">নগদ ছাড়া — শুধু জিজ্ঞেস, শিল্পীরা ব্যবস্থা জানাবেন</p>

                      <div className="mt-3 space-y-3">
                        <input
                          data-testid="enquiry-name-input"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="আপনার নাম"
                          className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                        />
                        <input
                          data-testid="enquiry-phone-input"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="ফোন নম্বর"
                          inputMode="tel"
                          className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                        />
                        <input
                          data-testid="enquiry-place-input"
                          value={place}
                          onChange={(e) => setPlace(e.target.value)}
                          placeholder="এলাকা — যেমন শান্তিনিকেতন"
                          className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                        />
                      </div>

                      {err && (
                        <p data-testid="enquiry-error" className="mt-3 text-xs text-vermilion">
                          {err}
                        </p>
                      )}

                      <button
                        data-testid="enquiry-submit"
                        onClick={submit}
                        disabled={busy}
                        className="mt-4 w-full rounded-full bg-vermilion py-3 text-sm font-medium text-alpana shadow-[0_10px_30px_rgba(200,62,43,0.35)] transition-all duration-300 hover:bg-[#d84a35] disabled:opacity-50"
                      >
                        {busy ? "পাঠানো হচ্ছে…" : "ডেলিভারি জিজ্ঞেস পাঠান"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-gold/15 px-5 py-4">
              <p className="text-center text-[11px] text-sand/40">
                প্রোটোটাইপ — জিজ্ঞেসগুলো গ্রামের খাতায় (ডেটাবেসে) জমা থাকে
              </p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
